import React, {useState} from 'react'
import {IconButton, InputAdornment, Stack, Typography, SxProps, Theme} from '@mui/material'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {NEUTRAL_SCALE, SEMANTIC} from 'src/theme-new/tokens'
import TextFieldNew from './TextFieldNew'

interface PasswordFieldNewProps {
  value: string
  onChange: (value: string) => void
  name?: string
  label?: string
  placeholder?: string
  autoComplete?: string
  fullWidth?: boolean
  required?: boolean
  disabled?: boolean
  error?: boolean
  helperText?: string
  showCapsLockWarning?: boolean
  capsLockMessage?: string
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  sx?: SxProps<Theme>
}

export default function PasswordFieldNew({
  value,
  onChange,
  name,
  label,
  placeholder,
  autoComplete = 'current-password',
  fullWidth,
  required,
  disabled,
  error,
  helperText,
  showCapsLockWarning = true,
  capsLockMessage = 'Bloc Maiusc attivo',
  onKeyDown,
  onBlur,
  sx,
}: PasswordFieldNewProps): JSX.Element {
  const [visible, setVisible] = useState(false)
  const [capsLockOn, setCapsLockOn] = useState(false)

  const handleKeyEvent = (e: React.KeyboardEvent<HTMLInputElement>) => {
    setCapsLockOn(e.getModifierState('CapsLock'))
    if (onKeyDown && e.type === 'keydown') onKeyDown(e)
  }

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
    setCapsLockOn(false)
    if (onBlur) onBlur(e)
  }

  const toggleAdornment = (
    <InputAdornment position="end">
      <IconButton
        onClick={() => setVisible(v => !v)}
        tabIndex={-1}
        aria-label={visible ? 'Nascondi password' : 'Mostra password'}
        edge="end"
        size="small"
        sx={{color: NEUTRAL_SCALE[500]}}
      >
        {visible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconButton>
    </InputAdornment>
  )

  return (
    <Stack spacing={0} sx={{width: fullWidth ? '100%' : undefined, ...sx}}>
      <TextFieldNew
        value={value}
        onChange={onChange}
        name={name}
        label={label}
        placeholder={placeholder}
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        fullWidth={fullWidth}
        required={required}
        disabled={disabled}
        error={error}
        helperText={helperText}
        endAdornment={toggleAdornment}
        onKeyDown={handleKeyEvent}
        onKeyUp={handleKeyEvent}
        onBlur={handleBlur}
      />
      {showCapsLockWarning && capsLockOn && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{mt: 0.5}}>
          <WarningAmberIcon sx={{fontSize: 14, color: SEMANTIC.warning.dark}} />
          <Typography variant="caption" sx={{color: SEMANTIC.warning.dark}}>
            {capsLockMessage}
          </Typography>
        </Stack>
      )}
    </Stack>
  )
}
